import type { RenderContext, SectionProps } from "../context";
import { field } from "../context";
import { initials } from "../helpers";
import { Icon } from "../icons";
import { CARD_HOVER, Container, REVEAL, Rail, STACK, STAGGER, SectionHeader, SectionShell, Stars, cx } from "../ui";

type Item = SectionProps<"testimonials">["node"]["content"]["items"][number];

function SampleBadge({ ctx, item }: { ctx: RenderContext; item: Item }) {
  if (!ctx.editor || !item.sample) return null;
  return (
    <span className="absolute top-4 right-4 rounded-full bg-amber-100 px-2 py-0.5 text-[0.6875rem] font-semibold tracking-wide text-amber-900 uppercase" title="Ganti dengan ulasan asli sebelum publish">
      Contoh
    </span>
  );
}

function Author({ ctx, item, i, center }: { ctx: RenderContext; item: Item; i: number; center?: boolean }) {
  return (
    <figcaption className={cx("flex items-center gap-3", center && "justify-center")}>
      <span aria-hidden="true" className="grid size-11 shrink-0 place-items-center rounded-full bg-site-primary/10 text-sm font-semibold text-site-primary">
        {initials(item.name)}
      </span>
      <span className={cx("flex flex-col", center && "text-left")}>
        <span className="font-semibold text-site-text" {...field(ctx, `content.items.${i}.name`)}>
          {item.name}
        </span>
        {item.role ? (
          <span className="text-sm text-site-muted" {...field(ctx, `content.items.${i}.role`)}>
            {item.role}
          </span>
        ) : null}
      </span>
    </figcaption>
  );
}

function Card({ ctx, item, i }: { ctx: RenderContext; item: Item; i: number }) {
  return (
    <figure className={cx("relative flex h-full flex-col rounded-site-md border border-site-border bg-site-surface p-6 shadow-site-sm @3xl:p-8", CARD_HOVER)}>
      <SampleBadge ctx={ctx} item={item} />
      <Icon name="quote" className="size-8 text-site-primary/25" />
      {item.rating ? <Stars rating={item.rating} className="mt-4" /> : null}
      <blockquote className="mt-4 flex-1 text-base leading-relaxed text-site-text @3xl:text-[1.0625rem]" {...field(ctx, `content.items.${i}.quote`)}>
        <p>{item.quote}</p>
      </blockquote>
      <div className="mt-6 border-t border-site-border pt-5">
        <Author ctx={ctx} item={item} i={i} />
      </div>
    </figure>
  );
}

export function Testimonials({ node, ctx }: SectionProps<"testimonials">) {
  const c = node.content;
  const items = c.items;

  if (node.variant === "single" && items.length > 0) {
    const [first, ...rest] = items;
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container size="narrow" className="text-center">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
          <figure className={cx(STACK, REVEAL, "relative")}>
            <SampleBadge ctx={ctx} item={first!} />
            <Icon name="quote" className="mx-auto size-12 text-site-primary/20" />
            {first!.rating ? <Stars rating={first!.rating} className="mt-5 justify-center" /> : null}
            <blockquote className="baze-heading mt-6 text-2xl leading-snug text-balance @3xl:text-[2rem]" {...field(ctx, "content.items.0.quote")}>
              <p>“{first!.quote}”</p>
            </blockquote>
            <div className="mt-8">
              <Author ctx={ctx} item={first!} i={0} center />
            </div>
          </figure>
        </Container>
        {rest.length > 0 ? (
          <Container className="mt-14">
            <ul className={cx(STAGGER, "grid gap-5 @2xl:grid-cols-2 @4xl:grid-cols-3")}>
              {rest.map((item, j) => (
                <li key={j + 1}>
                  <Card ctx={ctx} item={item} i={j + 1} />
                </li>
              ))}
            </ul>
          </Container>
        ) : null}
      </SectionShell>
    );
  }

  if (node.variant === "chat" && items.length > 0) {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container size="narrow">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
          <ul className={cx(STACK, STAGGER, "flex flex-col gap-6 @3xl:gap-8")}>
            {items.map((item, i) => {
              const mine = i % 2 === 1;
              return (
                <li key={i} className={cx("flex items-end gap-3", mine && "flex-row-reverse")}>
                  <span aria-hidden="true" className={cx("grid size-10 shrink-0 place-items-center rounded-full text-sm font-semibold", mine ? "bg-site-secondary/40 text-site-text" : "bg-site-primary/10 text-site-primary")}>
                    {initials(item.name)}
                  </span>
                  <figure className={cx("relative max-w-[85%] @3xl:max-w-[75%]", mine && "text-right")}>
                    <figcaption className={cx("mb-1.5 flex flex-wrap items-baseline gap-x-2 px-1 text-sm", mine && "justify-end")}>
                      <span className="font-semibold text-site-text" {...field(ctx, `content.items.${i}.name`)}>
                        {item.name}
                      </span>
                      {item.role ? (
                        <span className="text-site-muted" {...field(ctx, `content.items.${i}.role`)}>
                          {item.role}
                        </span>
                      ) : null}
                    </figcaption>
                    <div
                      className={cx(
                        "relative rounded-site-lg px-5 py-4 text-left shadow-site-sm @3xl:px-6",
                        mine ? "rounded-br-sm bg-site-primary text-site-on-primary" : "rounded-bl-sm border border-site-border bg-site-surface text-site-text",
                      )}
                    >
                      <SampleBadge ctx={ctx} item={item} />
                      <blockquote className="text-base leading-relaxed @3xl:text-[1.0625rem]" {...field(ctx, `content.items.${i}.quote`)}>
                        <p>{item.quote}</p>
                      </blockquote>
                      <div className={cx("mt-3 flex items-center justify-end gap-3 text-xs", mine ? "text-site-on-primary/80" : "text-site-muted")}>
                        {item.rating ? <Stars rating={item.rating} className={cx("mr-auto", mine && "text-site-on-primary!")} /> : null}
                        <span aria-hidden="true" className="inline-flex -space-x-2">
                          <Icon name="check" className="size-4" />
                          <Icon name="check" className="size-4" />
                        </span>
                      </div>
                    </div>
                  </figure>
                </li>
              );
            })}
          </ul>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "carousel" && items.length > 1) {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} />
          <div className={STACK}>
            <Rail label={`Ulasan: ${c.title}`}>
              {items.map((item, i) => (
                <li key={i} className="w-[85%] shrink-0 snap-start py-1 @md:w-[60%] @3xl:w-[44%] @5xl:w-[36%]">
                  <Card ctx={ctx} item={item} i={i} />
                </li>
              ))}
            </Rail>
          </div>
        </Container>
      </SectionShell>
    );
  }

  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align={items.length < 3 ? "center" : "left"} />
        <ul className={cx(STACK, STAGGER, "grid gap-5 @3xl:gap-6", items.length === 1 ? "mx-auto max-w-xl" : items.length === 2 ? "mx-auto max-w-4xl @2xl:grid-cols-2" : "@2xl:grid-cols-2 @4xl:grid-cols-3")}>
          {items.map((item, i) => (
            <li key={i}>
              <Card ctx={ctx} item={item} i={i} />
            </li>
          ))}
        </ul>
      </Container>
    </SectionShell>
  );
}
